import {
  type CustomerApplicationContract,
  type CustomerProfileContract,
  type DuplicateCustomerSignal,
  type ListCustomerApplicationsParams,
  ListCustomerApplicationsParamsSchema,
  type ListCustomerApplicationsResult,
  type ListCustomersParams,
  ListCustomersParamsSchema,
  type ListCustomersResult,
  type SalesUserContract,
  createDuplicateSignals,
} from '@/contracts/customers'
import { createSyntheticMeta } from '@/contracts/questionnaire'
import {
  isExistingCustomerChangeApplication,
  isFullNewCustomerApplication,
  normalizeEmail,
} from '@/lib/business-workflow'
import {
  buildSourceSummary,
  listQuestionnaireSourceRecords,
} from '@/lib/customer-application-binding'
import { loadFixture } from '@/lib/mock-api/persistent-store'

function findDuplicateSignals(applicationId: string): DuplicateCustomerSignal[] {
  const { applications, customers } = loadFixture().businessWorkflow
  const application = applications.find((item) => item.id === applicationId)
  if (!application) return []
  return createDuplicateSignals(application, customers)
}

function toApplicationContract(applicationId: string): CustomerApplicationContract {
  const state = loadFixture()
  const { applications } = state.businessWorkflow
  const application = applications.find((item) => item.id === applicationId) ?? applications[0]
  const sourceRecords = listQuestionnaireSourceRecords({
    forms: state.forms,
    responses: state.responses,
    sourceLinks: state.businessWorkflow.sourceLinks,
    applications: state.businessWorkflow.applications,
    salesUsers: state.businessWorkflow.salesUsers,
    formDetails: state.formDetails,
    fieldBindings: state.businessWorkflow.fieldBindings,
  })
  return {
    ...application,
    duplicateSignals: findDuplicateSignals(application.id),
    sourceSummary: buildSourceSummary(
      application,
      state.businessWorkflow.sourceLinks,
      sourceRecords,
    ),
    fieldValues: state.businessWorkflow.fieldValues[application.id] ?? [],
    sourceMeta: createSyntheticMeta(),
  }
}

function toCustomerContract(customerId: string): CustomerProfileContract {
  const { customers } = loadFixture().businessWorkflow
  const customer = customers.find((item) => item.id === customerId) ?? customers[0]
  return {
    ...customer,
    sourceMeta: createSyntheticMeta(),
  }
}

function matchesAnyKeyword(
  values: Array<string | number | undefined>,
  keyword: string | undefined,
) {
  if (!keyword) return true
  const normalized = keyword.trim().toLocaleLowerCase()
  if (!normalized) return true
  return values.some((value) =>
    String(value ?? '')
      .toLocaleLowerCase()
      .includes(normalized),
  )
}

function paginate<T>(items: T[], page: number, limit: number) {
  const start = (page - 1) * limit
  return items.slice(start, start + limit)
}

export async function listMockSalesUsers(): Promise<{
  data: SalesUserContract[]
  meta: ReturnType<typeof createSyntheticMeta>
}> {
  const { salesUsers } = loadFixture().businessWorkflow
  return {
    data: salesUsers.map((salesUser) => ({
      ...salesUser,
      sourceMeta: createSyntheticMeta(),
    })),
    meta: createSyntheticMeta(),
  }
}

export async function listMockCustomerApplications(
  params: ListCustomerApplicationsParams = {},
): Promise<ListCustomerApplicationsResult> {
  const parsed = ListCustomerApplicationsParamsSchema.parse(params)
  const contracts = loadFixture().businessWorkflow.applications.map((application) =>
    toApplicationContract(application.id),
  )
  const filtered = contracts.filter((application) => {
    const matchesForm = !parsed.formId || application.formId === parsed.formId
    const matchesOwner =
      !parsed.ownerEmail ||
      normalizeEmail(application.submittedByEmail) === normalizeEmail(parsed.ownerEmail)
    const matchesSupervisor =
      !parsed.supervisorEmail ||
      normalizeEmail(application.supervisorEmail) === normalizeEmail(parsed.supervisorEmail)
    const matchesStatus = parsed.status === 'all' || application.status === parsed.status
    const matchesKeyword = matchesAnyKeyword(
      [
        application.formTitle,
        application.companyName,
        application.taxId,
        application.contactName,
        application.contactEmail,
        application.submittedByName,
        application.supervisorName,
        application.needSummary,
      ],
      parsed.keyword,
    )
    return matchesForm && matchesOwner && matchesSupervisor && matchesStatus && matchesKeyword
  })
  return {
    data: paginate(filtered, parsed.page, parsed.limit),
    meta: {
      ...createSyntheticMeta(),
      total: filtered.length,
      page: parsed.page,
      limit: parsed.limit,
    },
  }
}

export async function getMockCustomerApplication(
  applicationId: string,
): Promise<CustomerApplicationContract> {
  return toApplicationContract(applicationId)
}

export async function listMockCustomers(
  params: ListCustomersParams = {},
): Promise<ListCustomersResult> {
  const parsed = ListCustomersParamsSchema.parse(params)
  const contracts = loadFixture().businessWorkflow.customers.map((customer) =>
    toCustomerContract(customer.id),
  )
  const filtered = sortCustomers(
    contracts.filter((customer) => {
      const matchesForm = !parsed.formId || customer.formId === parsed.formId
      const matchesOwner =
        !parsed.ownerEmail ||
        normalizeEmail(customer.ownerEmail) === normalizeEmail(parsed.ownerEmail)
      const matchesStatus = parsed.status === 'all' || customer.status === parsed.status
      const matchesFiling =
        parsed.filingStatus === 'all' || customer.filingStatus === parsed.filingStatus
      const matchesWorkflow =
        parsed.customerWorkflow === 'all' ||
        (parsed.customerWorkflow === 'new_customer' && isCustomerWorkflowNew(customer)) ||
        (parsed.customerWorkflow === 'existing_customer_change' &&
          isExistingCustomerChangeApplication(customer))
      const matchesKeyword = matchesAnyKeyword(
        [
          customer.formTitle,
          customer.companyName,
          customer.taxId,
          customer.contactName,
          customer.contactEmail,
          customer.ownerName,
          customer.supervisorName,
          customer.erpCustomerCode,
        ],
        parsed.keyword,
      )
      return (
        matchesForm &&
        matchesOwner &&
        matchesStatus &&
        matchesFiling &&
        matchesWorkflow &&
        matchesKeyword
      )
    }),
    parsed.sort,
  )
  return {
    data: paginate(filtered, parsed.page, parsed.limit),
    meta: {
      ...createSyntheticMeta(),
      total: filtered.length,
      page: parsed.page,
      limit: parsed.limit,
    },
  }
}

export async function getMockCustomer(customerId: string): Promise<CustomerProfileContract> {
  return toCustomerContract(customerId)
}

function isCustomerWorkflowNew(customer: CustomerProfileContract) {
  if (isFullNewCustomerApplication(customer)) return true
  return !isExistingCustomerChangeApplication(customer)
}

function sortCustomers<
  T extends Pick<
    CustomerProfileContract,
    'companyName' | 'erpCustomerCode' | 'ownerName' | 'updatedAt'
  >,
>(customers: T[], sort: NonNullable<ListCustomersParams['sort']>) {
  return [...customers].sort((left, right) => {
    if (sort === 'updated_asc') return left.updatedAt.localeCompare(right.updatedAt)
    if (sort === 'customer_code_asc') {
      return (
        compareText(left.erpCustomerCode, right.erpCustomerCode) ||
        compareText(left.companyName, right.companyName)
      )
    }
    if (sort === 'customer_code_desc') {
      return (
        compareText(right.erpCustomerCode, left.erpCustomerCode) ||
        compareText(left.companyName, right.companyName)
      )
    }
    if (sort === 'name_asc') return compareText(left.companyName, right.companyName)
    if (sort === 'name_desc') return compareText(right.companyName, left.companyName)
    if (sort === 'owner_asc') {
      return (
        compareText(left.ownerName, right.ownerName) ||
        compareText(left.companyName, right.companyName)
      )
    }
    return right.updatedAt.localeCompare(left.updatedAt)
  })
}

function compareText(left: string | null | undefined, right: string | null | undefined) {
  return String(left ?? '').localeCompare(String(right ?? ''), 'zh-Hant', {
    numeric: true,
    sensitivity: 'base',
  })
}
